import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Contract, JsonRpcProvider, parseEther, formatEther, MaxUint256 } from "ethers";
import { useWallet } from "@/context/WalletContext";
import { Panel, Pill } from "@/components/croc/ui";
import { fmtNum } from "@/lib/format";
import api from "@/lib/api";
import { CONTRACTS, ABIS, CHAIN } from "@/contracts/croc";
import { Repeat, Radio } from "lucide-react";

const ERC20_ABI = [
  "function approve(address spender, uint256 value) returns (bool)",
  "function allowance(address owner, address spender) view returns (uint256)",
  "function balanceOf(address) view returns (uint256)",
];

const EX_VIEW_ABI = ["function croc() view returns (address)"];
// CROC accepted by the exchange, read on-chain (guards against a mismatched CrocToken address in config)
const exchangeCroc = async (runner) => {
  try { return await new Contract(CONTRACTS.CrocExchange, EX_VIEW_ABI, runner).croc(); }
  catch { return CONTRACTS.CrocToken; }
};
const ERR = {
  "0xe450d38c": "Not enough CROC in your wallet for this amount",
  "0xfb8f41b2": "CROC approval missing — please approve and try again",
};
const friendlyError = (e) => {
  if (e?.code === "ACTION_REJECTED" || e?.code === 4001 || e?.info?.error?.code === 4001) return "Transaction cancelled in wallet";
  const data = e?.data || e?.info?.error?.data || e?.error?.data || "";
  const sel = typeof data === "string" ? data.slice(0, 10) : "";
  const msg = e?.reason || e?.info?.error?.message || e?.shortMessage || e?.message || "";
  if (/insufficient funds/i.test(msg)) return "Not enough BNB in your wallet to pay network gas";
  return ERR[sel] || (msg ? `Transaction failed: ${msg.slice(0, 140)}` : "Transaction failed");
};

export const ExchangePanel = ({ tierIndex = 0 }) => {
  const { address, getBrowserProvider, ensureTestnet, refreshUser } = useWallet();
  const [crocBal, setCrocBal] = useState(null);
  // TEMPORARY: direct CROC buys are disabled — only Sell & Burn is offered.
  // Flip to true to restore the Buy tab.
  const BUY_ENABLED = false;
  const [side, setSide] = useState(BUY_ENABLED ? "buy" : "sell");
  const [amount, setAmount] = useState("");
  const [quote, setQuote] = useState(null);
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.exchangeInfo().then(setInfo).catch(() => {}); }, []);
  const loadBal = React.useCallback(async () => {
    if (!address) return setCrocBal(null);
    try {
      const rp = new JsonRpcProvider(CHAIN.rpc);
      const bal = await new Contract(await exchangeCroc(rp), ERC20_ABI, rp).balanceOf(address);
      setCrocBal(parseFloat(formatEther(bal)));
    } catch { setCrocBal(null); }
  }, [address]);
  useEffect(() => { loadBal(); }, [loadBal]);
  useEffect(() => {
    const n = parseFloat(amount);
    if (!n || n <= 0) return setQuote(null);
    const t = setTimeout(() => api.exchangeQuote(side, n).then(setQuote).catch(() => setQuote(null)), 250);
    return () => clearTimeout(t);
  }, [amount, side]);

  const run = async () => {
    if (!getBrowserProvider()) return toast.error("Connect a real wallet (MetaMask/WalletConnect) to trade");
    const n = parseFloat(amount);
    if (!n || n <= 0) return toast.error("Enter an amount");
    try {
      setBusy(true);
      if (side === "buy" && !BUY_ENABLED) throw new Error("Direct CROC buys are temporarily disabled — use Auto-Stake above");
      await ensureTestnet();
      // fresh provider after a possible chain switch (ethers throws "network changed" on a stale one)
      const signer = await getBrowserProvider().getSigner();
      const me = await signer.getAddress();
      const ex = new Contract(CONTRACTS.CrocExchange, ABIS.CrocExchange, signer);
      const wei = parseEther(String(n));
      if (side === "buy") {
        const usdt = new Contract(CONTRACTS.MockUSDT, ERC20_ABI, signer);
        const allowance = await usdt.allowance(me, CONTRACTS.CrocExchange);
        if (allowance < wei) {
          toast.info("Approve USDT…");
          await (await usdt.approve(CONTRACTS.CrocExchange, MaxUint256)).wait();
        }
        toast.info("Buying CROC…");
        await (await ex.buy(wei, tierIndex)).wait();
        toast.success(`Bought CROC with ${n} USDT`);
      } else {
        // reads go through the public RPC; wallet RPCs (esp. mobile/WalletConnect) are flaky for eth_call
        const rp = new JsonRpcProvider(CHAIN.rpc);
        const crocAddr = await exchangeCroc(rp);
        const crocRead = new Contract(crocAddr, ERC20_ABI, rp);
        const bal = await crocRead.balanceOf(me);
        let sellWei = wei;
        if (sellWei > bal) {
          // MAX / typed balance is rounded for display — sell the exact wallet balance on tiny overshoot
          if (sellWei - bal <= parseEther("0.001")) sellWei = bal;
          else throw new Error(`Not enough CROC: wallet has ${fmtNum(parseFloat(formatEther(bal)), 4)} CROC`);
        }
        const allowance = await crocRead.allowance(me, CONTRACTS.CrocExchange);
        if (allowance < sellWei) {
          toast.info("Approve CROC…");
          await (await new Contract(crocAddr, ERC20_ABI, signer).approve(CONTRACTS.CrocExchange, MaxUint256)).wait();
        }
        const exRead = new Contract(CONTRACTS.CrocExchange, ABIS.CrocExchange, rp);
        await exRead.sell.staticCall(sellWei, { from: me });
        const gas = await exRead.sell.estimateGas(sellWei, { from: me });
        toast.info("Selling & burning CROC…");
        await (await ex.sell(sellWei, { gasLimit: (gas * 13n) / 10n })).wait();
        toast.success(`Sold ${n} CROC for USDT`);
      }
      setAmount("");
      refreshUser?.();
      loadBal();
      api.exchangeInfo().then(setInfo).catch(() => {});
    } catch (e) {
      toast.error(e?.message?.startsWith("Not enough CROC") ? e.message : friendlyError(e));
    } finally {
      setBusy(false);
    }
  };

  const inToken = side === "buy" ? "USDT" : "CROC";
  return (
    <Panel data-testid="exchange-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Repeat className="w-4 h-4 emerald-text" />
          <span className="font-display font-bold text-white">{BUY_ENABLED ? "Buy / Sell" : "Sell"} CROC · USDT</span>
        </div>
        <Pill tone="emerald"><Radio className="w-3 h-3" /> On-chain</Pill>
      </div>

      <div className={`grid ${BUY_ENABLED ? "grid-cols-2" : "grid-cols-1"} gap-2 mb-4`}>
        {BUY_ENABLED && (
          <button data-testid="exch-tab-buy" onClick={() => setSide("buy")}
            className={`rounded-xl py-2.5 font-semibold transition-colors ${side === "buy" ? "bg-[#1CFD4C] text-[#050806]" : "bg-[#0B120E] text-muted-foreground border border-white/10"}`}>Buy</button>
        )}
        <button data-testid="exch-tab-sell" onClick={() => setSide("sell")}
          className={`rounded-xl py-2.5 font-semibold transition-colors ${side === "sell" ? "bg-[#10B981] text-[#050806]" : "bg-[#0B120E] text-muted-foreground border border-white/10"}`}>Sell &amp; Burn</button>
      </div>

      <div className="flex items-center justify-between">
        <label className="text-xs text-muted-foreground">You pay ({inToken})</label>
        {side === "sell" && crocBal !== null && (
          <button type="button" data-testid="exch-max-btn" onClick={() => setAmount(String(Math.floor(crocBal * 10000) / 10000))}
            className="text-[11px] text-muted-foreground hover:text-white transition-colors">
            Wallet: <span className="font-num text-white" data-testid="exch-croc-balance">{fmtNum(crocBal, 2)} CROC</span> · <span className="emerald-text">MAX</span>
          </button>
        )}
      </div>
      <input data-testid="exch-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)}
        placeholder="0.00"
        className="w-full mt-1 rounded-xl border border-white/10 bg-[#090E0B] px-4 py-3 font-num text-white text-lg outline-none focus:border-[#1CFD4C]/50" />

      {quote?.ok && (
        <div className="mt-3 rounded-xl border border-white/10 bg-[#090E0B] p-3 space-y-1.5 text-sm" data-testid="exch-quote">
          {side === "buy" ? (
            <>
              <Row label="You receive" value={`${fmtNum(quote.croc_out, 2)} CROC`} accent />
              <Row label="Buy fee" value={`${fmtNum(quote.fee_usdt, 2)} USDT`} />
            </>
          ) : (
            <>
              <Row label="You receive" value={`${fmtNum(quote.net_usdt, 2)} USDT`} accent />
              <Row label="Swap charges" value={`${fmtNum(quote.fee_usdt, 2)} USDT`} />
              <Row label="CROC burned" value={`${fmtNum(quote.croc_in, 2)} 🔥`} />
            </>
          )}
          <Row label="Rate" value={`1 CROC = $${fmtNum(quote.price, 5)}`} />
        </div>
      )}

      <button data-testid="exch-submit" onClick={run} disabled={busy || (side === "buy" && !BUY_ENABLED)}
        className={`w-full mt-4 rounded-xl py-3 font-semibold text-[#050806] transition-colors disabled:opacity-60 ${side === "buy" ? "bg-[#1CFD4C] hover:bg-[#4DFF74]" : "bg-[#10B981] hover:bg-[#34D399]"}`}>
        {busy ? "Processing…" : side === "buy" ? "Approve & Buy CROC" : "Approve, Sell & Burn"}
      </button>

      {info && (
        <div className="mt-4 flex items-center justify-end text-[11px] text-muted-foreground">
          <a href={`${CHAIN.explorer}/address/${info.exchange_address}`} target="_blank" rel="noreferrer" className="emerald-text hover:underline">Contract ↗</a>
        </div>
      )}
    </Panel>
  );
};

function Row({ label, value, accent }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={`font-num ${accent ? "emerald-text font-bold" : "text-white"}`}>{value}</span>
    </div>
  );
}
